import { forwardRef, type ComponentPropsWithRef, type DetailedHTMLProps, type HTMLAttributes } from "react";

import { clamp } from "lodash";

import { Card, Circle, cn, Typography, type CardProps, type CircleProps } from "@eisox/design-system";

/* -------------------------------------------------------------------------------------------------
 * Sensor
 * -----------------------------------------------------------------------------------------------*/

type Root = CardProps;
const Root = forwardRef<HTMLDivElement, Root>(({ className, children, ...props }, ref) => (
  <Card
    {...props}
    ref={ref}
    className={cn(
      "flex h-full flex-1 flex-col items-center justify-center gap-[5px] p-[10px] min-[1400px]:w-[186px] min-[1400px]:gap-[15px] min-[1400px]:p-[20px]",
      className,
    )}
  >
    {children}
  </Card>
));

Root.displayName = "Sensor.Root";

/* -------------------------------------------------------------------------------------------------
 * Icon
 * -----------------------------------------------------------------------------------------------*/

type Icon = CircleProps;
const Icon = forwardRef<HTMLDivElement, Icon>(({ className, children, ...props }, ref) => (
  <Circle.Root
    {...props}
    ref={ref}
    color="white"
    backgroundColor="darkGray"
    className={cn(
      "h-[35px]! w-[35px]! rounded-[10px] min-[1400px]:h-[50px]! min-[1400px]:w-[50px]! min-[1400px]:rounded-full",
      className,
    )}
  >
    {children}
  </Circle.Root>
));

Icon.displayName = "Sensor.Icon";

/* -------------------------------------------------------------------------------------------------
 * Group
 * -----------------------------------------------------------------------------------------------*/

type Group = ComponentPropsWithRef<"div">;
const Group = forwardRef<HTMLDivElement, Group>(({ className, children, ...props }, ref) => (
  <div {...props} ref={ref} className={cn("flex flex-1 flex-row items-center justify-center gap-[5px]", className)}>
    {children}
  </div>
));
Group.displayName = "Sensor.Group";

/* -------------------------------------------------------------------------------------------------
 * Value
 * -----------------------------------------------------------------------------------------------*/

type Value = ComponentPropsWithRef<"div">;
const Value = forwardRef<HTMLDivElement, Value>(({ className, children, ...props }, ref) => (
  <div
    {...props}
    ref={ref}
    className={cn(
      "min-w-0 font-semibold w-full text-center text-base text-primary-dark-blue min-[1400px]:text-[clamp(1rem,2.5vw,1.5rem)]",
      className,
    )}
  >
    <Typography>{children}</Typography>
  </div>
));
Value.displayName = "Sensor.Value";

/* -------------------------------------------------------------------------------------------------
 * Unity
 * -----------------------------------------------------------------------------------------------*/

type Unity = ComponentPropsWithRef<"span">;
const Unity = forwardRef<HTMLSpanElement, Unity>(({ className, children, ...props }, ref) => (
  <span
    {...props}
    ref={ref}
    className={cn("font-normal align-top text-sm text-primary-dark-gray min-[1400px]:text-md", className)}
  >
    {children}
  </span>
));
Unity.displayName = "Sensor.Unity";

/* -------------------------------------------------------------------------------------------------
 * Gradient
 * -----------------------------------------------------------------------------------------------*/

interface Gradient extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  gradient: string;
  minValue: number;
  maxValue: number;
  value?: number;
  minText: string;
  maxText: string;
}

const Gradient = forwardRef<HTMLDivElement, Gradient>(
  ({ gradient, value, minText, maxText, minValue, maxValue, className, ...props }, ref) => {
    const jaugePosition = value !== undefined && clamp(((value - minValue) * 100) / (maxValue - minValue), 0, 100);

    return (
      <div
        {...props}
        ref={ref}
        className={cn(
          "hidden min-[1400px]:flex min-[1400px]:w-full min-[1400px]:flex-col min-[1400px]:gap-[6px]",
          className,
        )}
      >
        <div
          className="relative h-[8px] rounded-[11px]"
          style={{ background: `linear-gradient(to right, ${gradient})` }}
        >
          {typeof jaugePosition === "number" && (
            <span
              className="absolute h-full w-[8px] rounded-full border-2 border-primary-white"
              style={{ left: `${jaugePosition}%`, transform: `translateX(-${jaugePosition}%)` }}
            />
          )}
        </div>
        <div className="flex justify-between gap-[5px] text-base">
          <p>{minText}</p>
          <p>{maxText}</p>
        </div>
      </div>
    );
  },
);
Gradient.displayName = "Sensor.Gradient";

export { Root, Group, Icon, Value, Unity, Gradient };
